import { X } from "lucide-react";
import type { ComponentType } from "react";
import {
  FilterSelectionBadges,
  FILTER_BADGE_REMOVE_ICON_CLASS,
} from "../shared/filter_selection_badges";
import type { CityLike, ServiceLike } from "../shared/filter_selection_badges";
import {
  DEFAULT_ALPHABETICAL_SORT,
  DEFAULT_ALLIANCE_COUNT_SORT,
  type AlphabeticalSortOption,
  type AllianceCountSortOption,
  type FreelancerSearchFilterBadgesLabels,
  type ToolbarBadgeProps,
} from "./types";

export type { ServiceLike, CityLike };

export type FreelancerSearchFilterBadgesProps<
  TService extends ServiceLike,
  TCity extends CityLike,
> = {
  labels: FreelancerSearchFilterBadgesLabels;
  getLanguageDisplayLabel: (code: string) => string;
  selectedLanguages: string[];
  selectedServices: TService[];
  selectedCities: TCity[];
  rating: number;
  proximity: number;
  alphabeticalSort: AlphabeticalSortOption;
  allianceCountSort: AllianceCountSortOption;
  onRemoveLanguage: (code: string) => void;
  onRemoveService: (id: number) => void;
  onRemoveCity: (id: number) => void;
  onClearRating: () => void;
  onClearProximity: () => void;
  onResetAlphabeticalSort: () => void;
  onResetAllianceCountSort: () => void;
  Badge: ComponentType<ToolbarBadgeProps>;
  badgeRowClassName?: string;
  badgeFiltersClassName?: string;
  badgeSortsClassName?: string;
  formatRatingValue?: (rating: number) => string;
  formatProximityValue?: (km: number) => string;
};

export function FreelancerSearchFilterBadges<
  TService extends ServiceLike,
  TCity extends CityLike,
>({
  labels,
  getLanguageDisplayLabel,
  selectedLanguages,
  selectedServices,
  selectedCities,
  rating,
  proximity,
  alphabeticalSort,
  allianceCountSort,
  onRemoveLanguage,
  onRemoveService,
  onRemoveCity,
  onClearRating,
  onClearProximity,
  onResetAlphabeticalSort,
  onResetAllianceCountSort,
  Badge,
  badgeRowClassName = "mb-4 flex w-full flex-col gap-4 md:flex-row md:justify-between",
  badgeFiltersClassName = "flex flex-wrap gap-2",
  badgeSortsClassName = "flex flex-wrap gap-2 md:justify-end",
  formatRatingValue = (r: number) => `${r}+`,
  formatProximityValue = (km: number) => `${km}km`,
}: FreelancerSearchFilterBadgesProps<TService, TCity>) {
  const hasBadges =
    selectedLanguages.length > 0 ||
    selectedServices.length > 0 ||
    selectedCities.length > 0 ||
    rating > 0 ||
    proximity > 0 ||
    (alphabeticalSort && alphabeticalSort !== DEFAULT_ALPHABETICAL_SORT) ||
    (allianceCountSort && allianceCountSort !== DEFAULT_ALLIANCE_COUNT_SORT);

  if (!hasBadges) {
    return null;
  }

  return (
    <div className={badgeRowClassName}>
      <div className={badgeFiltersClassName}>
        <FilterSelectionBadges
          labels={labels}
          getLanguageDisplayLabel={getLanguageDisplayLabel}
          selectedLanguages={selectedLanguages}
          selectedServices={selectedServices}
          selectedCities={selectedCities}
          onRemoveLanguage={onRemoveLanguage}
          onRemoveService={onRemoveService}
          onRemoveCity={onRemoveCity}
          Badge={Badge}
        />
        {rating > 0 && (
          <Badge
            variant="light"
            color="light"
            size="md"
            endIcon={
              <X
                className={FILTER_BADGE_REMOVE_ICON_CLASS}
                onClick={() => {
                  onClearRating();
                }}
              />
            }
          >
            {labels.badgeRatingPrefix}: {formatRatingValue(rating)}
          </Badge>
        )}
        {proximity > 0 && (
          <Badge
            variant="light"
            color="light"
            size="md"
            endIcon={
              <X
                className={FILTER_BADGE_REMOVE_ICON_CLASS}
                onClick={() => {
                  onClearProximity();
                }}
              />
            }
          >
            {labels.badgeProximityPrefix}: {formatProximityValue(proximity)}
          </Badge>
        )}
      </div>

      <div className={badgeSortsClassName}>
        {alphabeticalSort && alphabeticalSort !== DEFAULT_ALPHABETICAL_SORT && (
          <Badge
            variant="light"
            color="light"
            size="md"
            endIcon={
              <X
                className={FILTER_BADGE_REMOVE_ICON_CLASS}
                onClick={() => {
                  onResetAlphabeticalSort();
                }}
              />
            }
          >
            {labels.badgeAlphabeticalPrefix}:{" "}
            {labels.sortAlphabeticalLabel(alphabeticalSort)}
          </Badge>
        )}
        {allianceCountSort &&
          allianceCountSort !== DEFAULT_ALLIANCE_COUNT_SORT && (
            <Badge
              variant="light"
              color="light"
              size="md"
              endIcon={
                <X
                  className={FILTER_BADGE_REMOVE_ICON_CLASS}
                  onClick={() => {
                    onResetAllianceCountSort();
                  }}
                />
              }
            >
              {labels.badgeAllianceCountPrefix}:{" "}
              {labels.sortAllianceLabel(allianceCountSort)}
            </Badge>
          )}
      </div>
    </div>
  );
}
